import React, { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { firestoreService } from "@/services/firestoreService";
import { exportAnalysisToPDF, exportToCSV } from "@/utils/export";

interface SavedAnalysis {
  id: string;
  userId: string;
  title: string;
  type: "transcript" | "filing" | "copilot" | "news";
  content: any;
  tags?: string[];
  pinned?: boolean;
  notes?: string;
  createdAt: any;
  companyId?: string;
  companyName?: string;
  ticker?: string;
}

export const SavedAnalyses: React.FC = () => {
  const { user } = useAuth();
  const [analyses, setAnalyses] = useState<SavedAnalysis[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<
    "all" | "transcript" | "filing" | "copilot" | "news"
  >("all");

  useEffect(() => {
    if (user) {
      loadAnalyses();
    }
  }, [user, filter]);

  const loadAnalyses = async () => {
    if (!user) return;

    try {
      setLoading(true);
      const constraints = [
        firestoreService.where("userId", "==", user.uid),
        firestoreService.orderBy("createdAt", "desc"),
      ];

      if (filter !== "all") {
        constraints.unshift(firestoreService.where("type", "==", filter));
      }

      const saved = await firestoreService.getDocuments<SavedAnalysis>(
        "savedAnalyses",
        constraints
      );
      setAnalyses(saved);
    } catch (error) {
      console.error("Failed to load saved analyses:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Are you sure you want to delete this saved analysis?"))
      return;

    try {
      await firestoreService.deleteDocument("savedAnalyses", id);
      setAnalyses((prev) => prev.filter((a) => a.id !== id));
    } catch (error) {
      console.error("Failed to delete analysis:", error);
      alert("Failed to delete analysis");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blazerex-green"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container mx-auto px-4">
        <div className="mb-8">
          <h1 className="h1 text-4xl mb-2">Saved Analyses</h1>
          <p className="body-text text-gray-600">
            Your saved transcript analyses, filing analyses, and copilot
            conversations
          </p>
        </div>

        <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
          <div className="flex gap-4 flex-wrap">
            {(["all", "transcript", "filing", "copilot", "news"] as const).map(
              (type) => (
                <Button
                  key={type}
                  variant={filter === type ? "primary" : "outline"}
                  size="sm"
                  onClick={() => setFilter(type)}
                >
                  {type === "all"
                    ? "All"
                    : type.charAt(0).toUpperCase() + type.slice(1)}
                </Button>
              )
            )}
          </div>
          {analyses.length > 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                const csvData = analyses.map((a) => ({
                  title: a.title,
                  type: a.type,
                  company: a.companyName || "",
                  ticker: a.ticker || "",
                  createdAt: new Date(
                    a.createdAt?.toDate?.() || a.createdAt
                  ).toLocaleDateString(),
                }));
                exportToCSV(csvData, "saved-analyses");
              }}
            >
              Export CSV
            </Button>
          )}
        </div>

        {analyses.length === 0 ? (
          <Card>
            <div className="text-center py-12">
              <p className="body-text text-gray-600">
                No saved analyses yet. Save analyses from transcripts, filings,
                or copilot conversations.
              </p>
            </div>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {analyses.map((analysis) => (
              <Card key={analysis.id} hover>
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1">
                    {analysis.pinned && (
                      <span className="text-xs text-blazerex-green mb-1 block">
                        📌 Pinned
                      </span>
                    )}
                    <h3 className="sub-header text-lg mb-1">
                      {analysis.title}
                    </h3>
                    {analysis.companyName && (
                      <p className="body-text text-sm text-gray-600">
                        {analysis.ticker} - {analysis.companyName}
                      </p>
                    )}
                  </div>
                </div>
                <p className="body-text text-xs text-gray-500 mb-3 capitalize">
                  {analysis.type} •{" "}
                  {new Date(
                    analysis.createdAt?.toDate?.() || analysis.createdAt
                  ).toLocaleDateString()}
                </p>
                {analysis.tags && analysis.tags.length > 0 && (
                  <div className="flex gap-2 mb-3 flex-wrap">
                    {analysis.tags.map((tag) => (
                      <span
                        key={tag}
                        className="px-2 py-1 bg-gray-100 rounded text-xs"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                )}
                {analysis.notes && (
                  <p className="body-text text-sm text-gray-600 mb-3 line-clamp-2">
                    {analysis.notes}
                  </p>
                )}
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      if (
                        analysis.type === "transcript" &&
                        analysis.content?.transcriptId
                      ) {
                        window.location.href = `/companies/${analysis.ticker}?transcript=${analysis.content.transcriptId}`;
                      } else if (
                        analysis.type === "filing" &&
                        analysis.content?.filingId
                      ) {
                        window.location.href = `/companies/${analysis.ticker}?filing=${analysis.content.filingId}`;
                      } else if (analysis.content?.analysis) {
                        exportAnalysisToPDF(
                          analysis.content.analysis,
                          analysis.title
                        );
                      }
                    }}
                  >
                    View
                  </Button>
                  {analysis.content?.analysis && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        exportAnalysisToPDF(
                          analysis.content.analysis,
                          analysis.title
                        )
                      }
                    >
                      Export
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDelete(analysis.id)}
                  >
                    Delete
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
